import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import IdolSelector from '@/components/ui/IdolSelector';
import Axios from '@/shared/api/axios';

// 表單初始值：第一次進頁面、或偶像尚未設定過訂閱方案（API 成功但沒有 data）時顯示
const INITIAL_BASE_FEE = 0;
const INITIAL_DISCOUNTS = {
  1: '0',
  3: '0',
  6: '0',
  12: '0'
};

function SubscriptionSettings() {
  const { t } = useTranslation();
  const [baseFee, setBaseFee] = useState(INITIAL_BASE_FEE);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdol, setSelectedIdol] = useState('');
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const successModalRef = useRef();
  const statusModalRef = useRef();

  // 表單目前載入的是哪個偶像的設定。切換偶像後尚未載入完成、或載入失敗時會與 selectedIdol 不一致，
  // 此時表單仍是前一個偶像的值，停用儲存，避免把它存進新選的偶像
  const [loadedIdolId, setLoadedIdolId] = useState(null);
  // 防亂序：快速切換偶像時，只套用最後一次請求的結果
  const fetchSeqRef = useRef(0);

  // 訂閱方案折扣
  const [discounts, setDiscounts] = useState(INITIAL_DISCOUNTS);

  // 折扣選項
  const discountOptions = [
    { value: '0', label: '0%' },
    { value: '5', label: '5%' },
    { value: '10', label: '10%' },
    { value: '20', label: '20%' },
    { value: '30', label: '30%' },
    { value: '40', label: '40%' },
    { value: '50', label: '50%' },
    { value: '100', label: '100%' }
  ];

  // 當選擇的偶像改變時，載入設定
  useEffect(() => {
    if (selectedIdol) {
      fetchSubscriptionPlan();
    }
  }, [selectedIdol]);

  // 取得偶像訂閱方案 (9-1)
  const fetchSubscriptionPlan = async () => {
    if (!selectedIdol) return;

    const requestIdol = selectedIdol;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const response = await Axios.post('/idol/get-subscription-plan', {
        idol_id: requestIdol,
        view_idol_id: requestIdol
      });
      if (seq !== fetchSeqRef.current) return;

      if (response.data?.status_code === 'SYSTEM_1000') {
        const data = response.data.data;
        if (data) {
          setBaseFee(data.monthly_price || 0);
          setDiscounts({
            1: String(data.discount_1_months || 0),
            3: String(data.discount_3_months || 0),
            6: String(data.discount_6_months || 0),
            12: String(data.discount_12_months || 0)
          });
        } else {
          // 尚未設定過訂閱方案：顯示初始值，不沿用前一個偶像留在表單上的值
          setBaseFee(INITIAL_BASE_FEE);
          setDiscounts(INITIAL_DISCOUNTS);
        }
        setHasChanges(false);
        setLoadedIdolId(requestIdol);
      } else if (response.data?.status_code) {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to fetch subscription plan:', error);
      }
    } finally {
      // 較舊的請求不關 loading：較新的請求還在途中
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  // 計算總計
  const calculateTotal = (months, discount) => {
    if (!baseFee) return 0;
    const total = baseFee * months;
    const discountValue = parseInt(discount);
    const finalAmount = discountValue === 0 ? total : total - (total * (discountValue / 100));
    // 如果是整數返回整數，否則最多兩位小數
    return finalAmount % 1 === 0 ? finalAmount : parseFloat(finalAmount.toFixed(2));
  };

  // 處理基礎月費變更
  const handleBaseFeeChange = (e) => {
    const value = e.target.value;
    // 允許空值或數字
    if (value === '') {
      setBaseFee('');
      setHasChanges(true);
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0) {
        setBaseFee(numValue);
        setHasChanges(true);
      }
    }
  };

  // 處理基礎月費失去焦點
  const handleBaseFeeBlur = () => {
    if (baseFee === '') {
      setBaseFee(0);
    }
  };

  // 處理折扣變更
  const handleDiscountChange = (months, value) => {
    setDiscounts(prev => ({
      ...prev,
      [months]: value
    }));
    setHasChanges(true);
  };

  // 儲存設定 (9-2)
  const handleSave = async () => {
    // 儲存鍵已在表單不是目前偶像的設定時停用，這裡再擋一次
    if (!selectedIdol || loadedIdolId !== selectedIdol) return;
    // 儲存途中若切換偶像，effect 已送出新偶像的讀取（序號前進）；回來後不可再動表單或重抓舊偶像，
    // 否則舊偶像的重抓會作廢新偶像的讀取，表單停在舊偶像、儲存鍵一直停用
    const seqAtSave = fetchSeqRef.current;

    setLoading(true);
    try {
      const response = await Axios.post('/idol/update-subscription-plan', {
        idol_id: selectedIdol,
        monthly_price: baseFee || 0,
        discount_1_months: parseInt(discounts[1]) || 0,
        discount_3_months: parseInt(discounts[3]) || 0,
        discount_6_months: parseInt(discounts[6]) || 0,
        discount_12_months: parseInt(discounts[12]) || 0
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        successModalRef.current?.show();
        if (seqAtSave !== fetchSeqRef.current) return;
        setHasChanges(false);
        // 重新載入訂閱方案
        fetchSubscriptionPlan();
      } else if (response.data?.status_code) {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to save subscription settings:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="mb-4">{t('subscription_settings')}</h3>

      {/* 偶像選擇器 */}
      <IdolSelector
        value={selectedIdol}
        onChange={setSelectedIdol}
        className="mb-4"
      />

      <div className="bg-white rounded p-4 mb-4">
        {/* 基礎月費 */}
        <div>
          <Form.Group>
            <Form.Label className="fw-bold">{t('base_monthly_fee')}</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                value={baseFee}
                onChange={handleBaseFeeChange}
                onBlur={handleBaseFeeBlur}
                min="0"
                style={{ width: '120px' }}
                disabled={loading}
              />
              <span>FCoin</span>
            </div>
            <Form.Text className="text-muted">
              {t('set_to_zero_when_empty')}
            </Form.Text>
          </Form.Group>
        </div>

        <hr className="my-4" />

        {/* 訂閱方案 */}
        <div className="mb-4">
          <h5 className="mb-4">{t('subscription_plans')}</h5>
          <div className="table-responsive">
            <table className="table">
              <thead className="table-light">
                <tr>
                  <th className="p-3 text-nowrap">{t('period')}</th>
                  <th className="p-3 text-nowrap">{t('discount_rate')}</th>
                  <th className="p-3 text-nowrap">{t('total')} (FCoin)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { months: 1, label: t('1_month') },
                  { months: 3, label: t('3_months') },
                  { months: 6, label: t('6_months') },
                  { months: 12, label: t('12_months') }
                ].map(({ months, label }) => (
                  <tr key={months}>
                    <td className="p-3 align-middle text-nowrap">{label}</td>
                    <td className="p-3 align-middle text-nowrap">
                      <Form.Select
                        value={discounts[months]}
                        onChange={(e) => handleDiscountChange(months, e.target.value)}
                        style={{ minWidth: '95px' }}
                        disabled={loading}
                      >
                        {discountOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="p-3 align-middle text-nowrap">
                      {calculateTotal(months, discounts[months])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 儲存按鈕 */}
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-primary rounded-pill px-4"
            onClick={handleSave}
            disabled={!hasChanges || loading || loadedIdolId !== selectedIdol}
          >
{t('save')}
          </button>
        </div>
      </div>

      {/* 成功訊息 Modal */}
      <MessageModal ref={successModalRef} status="success">
        {t('saved_successfully')}
      </MessageModal>

      {/* Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </div>
  );
}

export default SubscriptionSettings;
